<template>
    <ob1-cards
        data-test-id="device-card"
        class="device-card d-flex flex-column h-100"
        additional-classes="px-1 pt-2"
        :is-multiple="true"
        :is-selected="deviceInCart"
        :with-composite-link="false"
    >
        <a
            :href="device.detailsUrl"
            class="card-link-wrapper text-decoration-none text-dark d-block"
            :data-oevent-category="dataOeventCategory"
            :data-oevent-action="dataOeventAction"
            :data-oevent-label="dataOeventLabel"
        >

            <div class="d-flex flex-column h-100 justify-content-between">
                <ob1-tag
                    v-if="device.banner"
                    class="banner"
                    :label="device.banner"
                />
                <div class="d-flex flex-row">
                    <div v-if="device.image" class="img-device m-1">
                        <selector-color-chips :colors="device.availableColors" />
                        <div class="image-container">
                            <ob1-picture
                                class="device-picture"
                                :visual="device.image"
                            />
                        </div>
                    </div>
                    <div class="infos pr-1">
                        <div class="mobile-infos">
                            <div>{{ device.brand }}</div>
                            <h2 class="no-signature standard py-0 my-0 fw-normal">
                                <strong class="standard">
                                    {{ device.name }}
                                </strong>
                            </h2>
                            <Rating
                                v-if="device.reviewsStats"
                                :average="device.reviewsStats.average"
                                :rating-results-height="1.010"
                                :font-size="0.875"
                            />
                            <ob1-capsule
                                v-if="device.refurbished"
                                label="Reconditionné"
                                is-context
                                is-small
                            />
                            <ob1-tag
                                v-if="device.availabilityStatus && device.availabilityStatus.label"
                                class="mt-1"
                                type="unavailable"
                                :label="device.availabilityStatus.label"
                            />
                        </div>
                        <div class="infos-price">
                            <device-price
                                :compact-price="true"
                                :is-cents-format="true"
                                :multiple-price="device.multiplePrices"
                                :total-price-with-promo="device.discountedPriceInCents"
                                :total-price-without-promo="getPriceWithoutPromo()"
                                :monthly-amount-in-cents="getMonthlyAmountInCents()"
                                :monthly-duration="getMonthlyDuration()"
                                :generic-label="getPaymentInfoLabel()"
                                :loan-rate-zero-label="getZeroRateLabel()"
                                :is-price-inversion-enabled="isPriceInversionEnabled()"
                                :advantage-accessibility="advantageAccessibility"
                            />
                        </div>
                    </div>
                </div>
                <div class="infos-advantage py-1">
                    <div v-if="device.advantageValues">
                        <advantage-bloc
                            :advantage-messages="advantageMessages"
                            :discount="device.advantageValues.discount"
                            :odr="device.advantageValues.odr"
                            :bonus-recovery="device.advantageValues.bonusRecovery"
                        />
                    </div>
                </div>
                <!--/* Déjà dans votre panier message */-->
                <div class="infos-cart">
                    <div class="px-1 pb-1">
                        <ob1-message-alerte
                            v-if="deviceInCart"
                            type="success"
                            title="Déjà dans votre panier"
                        />
                    </div>
                </div>
                <div class="infos-seller">
                    <sold-by-card
                        :title="sellerLabel"
                        :sold-by-orange="device.firstParty"
                    />
                </div>
            </div>
        </a>
        <div class="d-flex align-items-center payment-solutions-details gap-xxl-2 pt-1">
            <!--/* Das */-->
            <das-tooltip
                v-if="showDasTooltip(device)"
                :id="'tooltip-device' + index"
                class=""
                :das="device.dasAccessible"
                :aria-label="dasAriaLabel(device.brand, device.name)"
            />
            <!--/* Energy-Label */-->
            <energy-label
                v-if="device.energyLabel"
                :id="device.seoId"
                class="energy-label-padding ms-auto"
                :energy-label="device.energyLabel"
                :with-link="false"
                :with-custom-image-class="true"
                :with-popin="false"
                data-oevent-category="selecteur_produit"
                @energyLabelClick="handleEnergyLabelClick"
            />
            <!--/* FSM - 4xCB */-->
            <FundingDetailsLink
                v-if="showFundingDetailsLink(device)"
                class="ms-auto"
                :seo-id="device.seoId"
                :text="fundingDetails.linkText"
                :device-name="device.brand + ' ' + device.name"
                @fundingDetailsClick="handleFundingDetailsClick"
            />
        </div>
    </ob1-cards>
</template>

<script>
import Rating from "../../../common/components/ob1/Rating.vue";
import SoldByCard from "../../../common/components/custom/SoldByCard.vue";
import AdvantageBloc from "../../common/fragments/AdvantageBloc.vue";
import SelectorColorChips from "../../common/selector/fragment/SelectorColorChips.vue";
import DasTooltip from "../../common/DasTooltip.vue";
import EnergyLabel from "../../common/EnergyLabel.vue";
import FundingDetailsLink from "../../common/FundingDetailsLink.vue";
import DevicePrice from "../../../common/components/ob1/DevicePrice.vue";

export default {
    name: "DeviceCard",
    components: {
        DevicePrice,
        FundingDetailsLink,
        EnergyLabel,
        DasTooltip,
        SelectorColorChips,
        AdvantageBloc,
        SoldByCard,
        Rating
    },
    props: {
        device: {
            type: Object,
            required: true
        },
        advantageMessages: {
            type: Object,
            required: true
        },
        hasThirdPartiesInCart: {
            type: Boolean,
            default: false
        },
        enablePriceInversion: {
            type: Boolean,
            default: false
        },
        fundingDetails: {
            type: Object,
            required: true
        },
        index: {
            type: Number,
            required: true
        },
        deviceInCartSeoId: {
            type: String,
            required: false,
            default: null
        },
        searchMode: {
            type: Boolean,
            default: false
        },
        textSearch: {
            type: String,
            default: null
        }
    },
    computed: {
        sellerLabel() {
            return 'Vendu et expédié par <b>' + this.device.seller + '</b>';
        },
        deviceInCart(){
            return this.device.seoId === this.deviceInCartSeoId || (this.device.alternativeVersionsSeoId.includes(this.deviceInCartSeoId));
        },
        advantageAccessibility() {
            const advantages = this.device.advantageValues;
            if (!advantages) {
                return null;
            }
            const parts = [];
            if (advantages.discount) {
                parts.push("grâce à " + this.advantageMessages.discount.replace(
                    "{0}",
                    PriceFormatter.formatEurosWithSpace(advantages.discount / 100, false).toString()
                ));
            }
            return parts.length ? parts.join(', ') : null;
        },
        dataOeventCategory() {
            return this.searchMode ? 'lr-orange-produit' : 'selecteur_produit';
        },
        dataOeventAction() {
            const pos = String(this.index + 1).padStart(2, '0');
            return this.searchMode ? 'clic-pos' + pos + '-produit-' + this.device.name : 'bloc_' + this.device.name.split(' ').join('_');
        },
        dataOeventLabel() {
            return this.searchMode ? this.textSearch : this.device.detailsUrl;
        }
    },
    methods: {
        getPriceWithoutPromo() {
            if (this.device.discountedPriceInCents === this.device.priceInCents) {
                return null;
            }
            return this.device.priceInCents;
        },
        handleCardClick(link) {
            window.location.href = link
        },
        handleFundingDetailsClick(seoId) {
            this.$emit('fundingDetailsClick', seoId)
        },
        handleEnergyLabelClick(seoId) {
            this.$emit('energyLabelClick', seoId)
        },
        showDasTooltip: function (device) {
            return device.dasAccessible;
        },
        showFundingDetailsLink: function (device) {
            return device.hasInstallmentPlan && !this.hasThirdPartiesInCart;
        },
        getMonthlyAmountInCents() {
            return this.hasThirdPartiesInCart ? null : this.device.monthlyPayment?.monthlyAmountInCents;
        },
        getMonthlyDuration() {
            return this.hasThirdPartiesInCart ? null : this.device.monthlyPayment?.duration;
        },
        getPaymentInfoLabel() {
            return this.hasThirdPartiesInCart ? null : this.device.monthlyPayment?.genericLabel;
        },
        getZeroRateLabel() {
            if (this.hasThirdPartiesInCart) return null;
            const monthlyPaymentTaegRate = this.device.monthlyPayment?.taegRate;
            const isEligibleForZeroRate = this.device.eligibleForZeroRate;
            if (monthlyPaymentTaegRate === 0) {
                return this.device.zeroRateLabel;
            }
            if (monthlyPaymentTaegRate == null && isEligibleForZeroRate) {
                return this.device.zeroRateOfferLabel;
            }
            return null;
        },
        isPriceInversionEnabled() {
            return this.enablePriceInversion && !this.hasThirdPartiesInCart && !!this.device.monthlyPayment?.monthlyAmountInCents;
        },
        dasAriaLabel(brand, name) {
            return brand ? 'DAS ' + brand + ' ' + name : 'DAS ' + name;
        }
    }
};
</script>

<style lang="scss">
@import 'ob1_common';
@import 'ob1_variables';

.banner {
    position: absolute;
    top: 0;
    left: 0;
}

.device-picture img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.image-container {
    position: relative;
    width: px-to-rem(127);
    height: px-to-rem(127);
    overflow: hidden; // Masque le débordement
}

.img-device {
    display: flex;
    align-items: center;
    flex-direction : column;
    width: px-to-rem(120);
    height: px-to-rem(120);
    position: relative;
}

.energy-label-padding {
    padding: .4375rem 0 .625rem;
}

//Surcharge car une avec l'option ob1-card-multiple pas de changement de bordure au hover
.device-card.ob1-card.ob1-card-multiple:hover {
        border-color: #555 !important;
        box-shadow: .125rem .125rem #555 !important;
}
.device-card.ob1-card.ob1-card-multiple.ob1-card-selected {
    border-color: #f15e00 !important;
    box-shadow: .125rem .125rem #f15e00 !important;
}
</style>
